import * as React from 'react';
import {Slot} from '@radix-ui/react-slot';
import {cva, type VariantProps} from 'class-variance-authority';
import {cn} from '../../../lib/utils';

const buttonVariants = cva(
	'hover:bg-opacity-20 inline-flex items-center justify-center text-white uppercase gap-2 whitespace-nowrap rounded-[6px] text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*=\'size-\'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-all duration-300',
	{
		variants: {
			variant: {
				default: 'bg-default text-base font-normal tracking-wide normal-case hover:text-default border border-solid border-default',
				secondary: 'bg-blue text-base font-normal tracking-wide normal-case hover:text-blue border border-solid border-blue',
				calendarDay: 'text-gray-600',
				outlined: 'border border-solid border-gray-300 text-gray-500',
				success: 'bg-success text-base font-normal tracking-wide',
				error: 'bg-error text-base font-normal tracking-wide',
				warning: 'bg-warning text-base font-normal tracking-wide',
				info: 'bg-info text-base font-normal tracking-wide',
				ghost: 'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
				link: 'text-primary underline-offset-4 hover:underline',
				black: 'bg-secondary-btn text-base font-normal tracking-wide normal-case hover:text-secondary-btn border border-solid border-secondary-btn',
				white: 'bg-white text-secondary-btn hover:bg-secondary-btn/20 font-normal tracking-wide normal-case hover:text-secondary-btn border border-solid border-secondary-btn'
			},
			size: {
				default: 'h-9 px-4 py-2 has-[>svg]:px-3',
				sm: 'h-8 gap-1.5 px-3 has-[>svg]:px-2.5',
				lg: 'h-10  px-6 has-[>svg]:px-4',
				icon: 'size-9'
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'default'
		}
	}
);

const Button = React.forwardRef<
	HTMLButtonElement,
	React.ComponentProps<'button'> &
	VariantProps<typeof buttonVariants> & {
	asChild?: boolean
}
>(({
	className,
	variant,
	size,
	asChild = false,
	...props
}, ref) => {
	const Comp = asChild ? Slot : 'button';
	return (
		<Comp
			ref={ref}
			data-slot="button"
			className={cn(buttonVariants({
				variant,
				size,
				className
			}))}
			{...props}
		/>
	);
});

export {Button, buttonVariants};
